<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { isToday, isPast } from 'date-fns';

import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmTaskDeleteDialog from 'dashboard/components-next/Tasks/ConfirmTaskDeleteDialog.vue';
import TaskRecurrenceScopeDialog from 'dashboard/components-next/Tasks/TaskRecurrenceScopeDialog.vue';
import {
  describeRule,
  getTaskDueDate,
  formatTaskEndTime,
} from 'dashboard/helper/taskRecurrence';

const props = defineProps({
  task: { type: Object, required: true },
  isDeleting: { type: Boolean, default: false },
});

const emit = defineEmits(['edit', 'close', 'toggleStatus', 'deleteTask']);

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();

const confirmDeleteDialogRef = ref(null);
const scopeDialogRef = ref(null);

const intlLocale = computed(() => locale.value.replace('_', '-'));
const dueDate = computed(() => getTaskDueDate(props.task));
const isCompleted = computed(() => props.task.status === 'completed');

const statusColorClass = computed(() => {
  if (isCompleted.value) return 'bg-n-slate-8';
  if (isPast(dueDate.value) && !isToday(dueDate.value)) return 'bg-n-ruby-9';
  if (isToday(dueDate.value)) return 'bg-n-teal-9';
  return 'bg-n-slate-10';
});

const formattedDate = computed(() => {
  const dateLabel = new Intl.DateTimeFormat(intlLocale.value, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(dueDate.value);
  if (props.task.allDay) {
    return `${dateLabel} · ${t('TASKS.DETAILS.ALL_DAY')}`;
  }
  const time = new Intl.DateTimeFormat(intlLocale.value, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(dueDate.value);
  return `${dateLabel} · ${time}${formatTaskEndTime(props.task, intlLocale.value)}`;
});

const recurrenceLabel = computed(() =>
  describeRule(props.task.recurrenceRule, dueDate.value, t, intlLocale.value)
);

const requestDelete = async () => {
  if (!props.task.recurrenceRule) {
    confirmDeleteDialogRef.value?.open();
    return;
  }
  const scope = await scopeDialogRef.value?.ask('delete');
  if (scope) {
    emit('deleteTask', props.task.id, {
      scope,
      occurrenceDate: props.task.occurrenceDate,
    });
  }
};

const linkedEntities = computed(() =>
  [
    {
      key: 'contact',
      label: t('TASKS.CREATE.FIELDS.LINK_TYPE_CONTACT'),
      icon: 'i-lucide-user',
      routeName: 'contacts_edit',
      param: 'contactId',
    },
    {
      key: 'company',
      label: t('TASKS.CREATE.FIELDS.LINK_TYPE_COMPANY'),
      icon: 'i-lucide-building-2',
      routeName: 'companies_dashboard_show',
      param: 'companyId',
    },
    {
      key: 'deal',
      label: t('TASKS.CREATE.FIELDS.LINK_TYPE_DEAL'),
      icon: 'i-lucide-handshake',
      routeName: 'deals_edit',
      param: 'dealId',
    },
  ]
    .filter(({ key }) => props.task[key])
    .map(({ key, label, icon, routeName, param }) => ({
      key,
      label,
      icon,
      name: props.task[key].name,
      location: {
        name: routeName,
        params: {
          accountId: route.params.accountId,
          [param]: props.task[key].id,
        },
      },
    }))
);

// Marca a rota de Tarefas com o taskId antes de sair, para o "voltar" reabrir
// esta tarefa (ver openTaskFromQuery em TasksIndex).
const openEntity = async location => {
  await router.replace({ query: { ...route.query, taskId: props.task.id } });
  router.push(location);
};
</script>

<template>
  <div class="flex flex-col gap-5">
    <div class="flex items-start gap-4">
      <span class="size-4 mt-2 rounded shrink-0" :class="statusColorClass" />
      <div class="flex flex-col flex-1 min-w-0 gap-1">
        <h3
          class="m-0 text-2xl break-words"
          :class="
            isCompleted ? 'line-through text-n-slate-10' : 'text-n-slate-12'
          "
        >
          {{ task.title }}
        </h3>
        <span class="text-sm text-n-slate-11 first-letter:uppercase">
          {{ formattedDate }}
        </span>
        <span v-if="recurrenceLabel" class="text-sm text-n-slate-11">
          {{ recurrenceLabel }}
        </span>
      </div>
      <div class="flex items-center gap-1 shrink-0 -mt-1 -mr-2">
        <Button
          icon="i-lucide-pencil"
          variant="ghost"
          color="slate"
          size="sm"
          :title="t('TASKS.DETAILS.EDIT')"
          @click="emit('edit')"
        />
        <Button
          icon="i-lucide-trash-2"
          variant="ghost"
          color="slate"
          size="sm"
          :title="t('TASKS.LIST.DELETE.CONFIRM')"
          @click="requestDelete"
        />
        <Button
          icon="i-lucide-x"
          variant="ghost"
          color="slate"
          size="sm"
          :title="t('TASKS.DETAILS.CLOSE')"
          @click="emit('close')"
        />
      </div>
    </div>

    <p
      v-if="task.description"
      class="m-0 text-sm text-n-slate-11 break-words whitespace-pre-line"
    >
      {{ task.description }}
    </p>

    <div
      class="flex flex-col rounded-xl border border-n-weak divide-y divide-n-weak"
    >
      <div
        v-for="entity in linkedEntities"
        :key="entity.key"
        class="flex items-center gap-3 px-4 py-3"
      >
        <span
          class="flex items-center justify-center size-8 rounded-lg bg-n-alpha-2 shrink-0"
        >
          <span class="size-4 text-n-slate-11" :class="entity.icon" />
        </span>
        <div class="flex flex-col min-w-0">
          <span class="text-xs text-n-slate-11">{{ entity.label }}</span>
          <a
            :href="router.resolve(entity.location).href"
            class="inline-flex items-center gap-1 min-w-0 text-sm font-medium text-n-brand hover:underline"
            @click.exact.prevent="openEntity(entity.location)"
          >
            <span class="truncate">{{ entity.name }}</span>
            <span class="i-lucide-arrow-up-right size-3.5 shrink-0" />
          </a>
        </div>
      </div>
      <div v-if="task.assignedTo" class="flex items-center gap-3 px-4 py-3">
        <span
          class="flex items-center justify-center size-8 rounded-lg bg-n-alpha-2 shrink-0"
        >
          <span class="i-lucide-user-round size-4 text-n-slate-11" />
        </span>
        <div class="flex flex-col min-w-0">
          <span class="text-xs text-n-slate-11">
            {{ t('TASKS.CREATE.FIELDS.ASSIGNED_TO') }}
          </span>
          <span class="text-sm font-medium text-n-slate-12 truncate">
            {{ task.assignedTo.name }}
          </span>
        </div>
      </div>
    </div>

    <div>
      <Button
        :label="
          isCompleted ? t('TASKS.ACTIONS.REOPEN') : t('TASKS.ACTIONS.COMPLETE')
        "
        :variant="isCompleted ? 'faded' : 'solid'"
        :color="isCompleted ? 'slate' : 'teal'"
        size="sm"
        @click="emit('toggleStatus')"
      />
    </div>

    <TaskRecurrenceScopeDialog ref="scopeDialogRef" />

    <ConfirmTaskDeleteDialog
      ref="confirmDeleteDialogRef"
      :task="{ id: task.id, title: task.title }"
      :is-loading="isDeleting"
      @confirm="emit('deleteTask', task.id)"
    />
  </div>
</template>
